<i18n src="./resources/locales.json"></i18n>

<template>
  <section class="stats-bar" :dark="$vuetify.theme.dark">
    <div class="evolve-contact">
      <div class="logo">{{ isSupervisor ? 'eSupervisor' : 'eAgent' }}</div>
    </div>
    <div v-if="showBPs" ref="business-processes" class="business-processes">
      <div
        v-for="(businessProcess, index) in businessProcessesStats"
        :key="`business-process_${index}`"
        :ref="`business-process_${index}`"
        class="business-process"
      >
        <div class="business-process-title">{{ businessProcess.businessProcessName }}</div>
        <div class="vertical-divider"></div>
      </div>
    </div>
    <div ref="business-process-details" class="business-process-details">
      <div v-if="!activeBp.agentStats" class="detail-title-row">
        <span class="inline-stat">
          <span v-once class="stat-title"> {{ $t('waitingInteractions') }}: </span>
          <span class="stat-value">
            {{ kpiFormat((activeBp.businessProcessWaitingInteractions || {}).value, 'number') }}
          </span>
        </span>
        <span class="inline-stat">
          <span v-once class="stat-title"> {{ $t('longestWait') }}: </span>
          <span class="stat-value">
            {{ kpiFormat((activeBp.businessProcessLongestWaitingTime || {}).value, 'duration', true) }}
          </span>
        </span>
        <span class="inline-stat">
          <span v-once class="stat-title"> {{ $t('serviceLevel') }}: </span>
          <span class="stat-value">
            {{ kpiFormat((activeBp.businessProcessServiceLevel || {}).value, 'percentage') }}
          </span>
        </span>
        <span class="inline-stat">
          <span v-once class="stat-title"> {{ $t('abandonedInteractions') }}: </span>
          <span class="stat-value">
            {{ kpiFormat((activeBp.businessProcessAbandonedInteractions || {}).value, 'number') }}
          </span>
        </span>

        <span class="inline-stat">
          <!--Avg Speed of Answer-->
          <span v-once class="stat-title"> {{ $t('avgSpeedOfAnswer') }}: </span>
          <span class="stat-value">
            {{ kpiFormat((activeBp.businessProcessAverageAnswerTime || {}).value, 'duration', true) }}
          </span>
        </span>

        <span class="inline-stat">
          <!--Avg Handling Time-->
          <span v-once class="stat-title"> {{ $t('avgHandlingTime') }}: </span>
          <span class="stat-value">
            {{ kpiFormat((activeBp.businessProcessAverageAgentHandlingTime || {}).value, 'duration', true) }}
          </span>
        </span>
      </div>
      <div v-else class="detail-description-row">
        <span class="inline-stat">
          <!--Agent login time span-->
          <span v-once class="stat-title"> {{ $t('loginTime') }}: </span>
          <span class="stat-value">
            {{ loggedInAgentStats.onlineTime }}
          </span>
        </span>
        <span class="inline-stat">
          <!--Agent average treating time-->
          <span v-once class="stat-title"> {{ $t('aht') }}: </span>
          <span class="stat-value">
            {{ kpiFormat((loggedInAgentStats.agentAverageTreatingTime || {}).value, 'duration', true) }}
          </span>
        </span>
        <span class="inline-stat">
          <!--Agent BP answered calls-->
          <span v-once class="stat-title"> {{ $t('callsHandled') }}: </span>
          <span class="stat-value">
            {{ kpiFormat((loggedInAgentStats.agentBPAnsweredCalls || {}).value, 'number') }}
          </span>
        </span>
        <span class="inline-stat">
          <!--Sum of Agent missed calls + Agent rejected calls -->
          <span v-once class="stat-title"> {{ $t('callsMissed') }}: </span>
          <span class="stat-value">
            {{ kpiFormat((loggedInAgentStats.agentMissedCalls || {}).value || 0, 'number') }}
          </span>
        </span>
        <span class="inline-stat">
          <!--Agent total not ready time-->
          <span v-once class="stat-title"> {{ $t('notReadyTime') }}: </span>
          <span class="stat-value">
            {{ kpiFormat((loggedInAgentStats.agentTotalNotReadyTime || {}).value, 'duration', true) }}
          </span>
        </span>
        <span class="inline-stat">
          <!--Agent AVG Handling Time-->
          <span v-once class="stat-title"> {{ $t('avgHandlingTime') }}: </span>
          <span class="stat-value">
            {{ kpiFormat((loggedInAgentStats.agentAverageTreatingTime || {}).value, 'duration', true) }}
          </span>
        </span>
        <span class="inline-stat">
          <!--Agent ASA-->
          <span v-once class="stat-title"> {{ $t('avgSpeedOfAnswer') }}: </span>
          <span class="stat-value">
            {{ kpiFormat((loggedInAgentStats.agentASA || {}).value, 'seconds') }}
          </span>
        </span>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  addClass,
  addClassToNextItem,
  delay,
  removeClass
} from '@/components/ecs-agent-stats/ecs-stats-summary-bar-header/domHelpers';
import EcsKpiFormatter from '@/mixins/ecs-kpi-formatter/EcsKpiFormatter';
import BusinessProcess from '@/classes/BusinessProcess';

export default {
  name: 'EcsStatsSummaryBarHeader',

  mixins: [EcsKpiFormatter],

  data() {
    return {
      activeBp: {
        businessProcessWaitingInteractions: {},
        businessProcessLongestWaitingTime: {},
        businessProcessServiceLevel: {},
        businessProcessAbandonedInteractions: {}
      },
      intervalReference: null
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent', 'isSupervisor', 'isSupervisorOnly']),

    ...mapGetters('STATS', ['BUSINESS_PROCESSES_STATS', 'SUPERVISED_BUSINESS_PROCESSES_STATS']),

    businessProcessesStats() {
      if (this.isSupervisorOnly && this.$featuresManager.supervisorFeatures.enable) {
        return [...this.SUPERVISED_BUSINESS_PROCESSES_STATS].sort(BusinessProcess.compare);
      }

      const additionalBpsToDisplay = this.SUPERVISED_BUSINESS_PROCESSES_STATS.filter(({ businessProcessId }) => {
        return !this.BUSINESS_PROCESSES_STATS.find(bp => bp.businessProcessId === businessProcessId);
      });

      let stats = [];
      if (!this.isSupervisorOnly) {
        stats.push({
          businessProcessName: this.$t('myStats'),
          agentStats: true
        });
      }
      stats = [
        ...stats,
        ...[...this.BUSINESS_PROCESSES_STATS, ...additionalBpsToDisplay].sort(BusinessProcess.compare)
      ];

      return stats;
    },

    loggedInAgentStats() {
      const defaultValues = {
        agentLoginTimeSpan: {},
        agentAverageTreatingTime: {},
        agentBPAnsweredCalls: {},
        agentMissedCalls: {},
        agentRejectedCalls: {},
        agentTotalNotReadyTime: {}
      };
      return this.$store.getters['STATS/loggedInAgentStats'] || defaultValues;
    },

    showBPs() {
      return this.businessProcessesStats.length > +!this.isSupervisorOnly;
    }
  },

  watch: {
    businessProcessesStats: {
      async handler() {
        await this.$nextTick();
        this.initializeAnimations();
      },
      deep: true
    }
  },

  created() {
    this.initializeAnimations();
  },

  beforeDestroy() {
    clearInterval(this.intervalReference);
  },

  mounted() {},

  methods: {
    initializeAnimations() {
      if (this.intervalReference || this.businessProcessesStats.length === +!this.isSupervisorOnly) {
        return;
      }

      const bpsContainer = this.$refs['business-processes'];
      const bpDetailsContainer = this.$refs['business-process-details'];
      if (!bpsContainer || !bpDetailsContainer) {
        return;
      }

      const bps = bpsContainer.querySelectorAll('.business-process');
      if (!this.activeBp) {
        this.activeBp = this.businessProcessesStats[0];
      }

      const showHeaders = () => addClass(bpsContainer, 'show-bp-headers');
      const setActiveBpData = () => {
        const index = [...bps].findIndex(element => element.classList.contains('active'));
        this.activeBp = this.businessProcessesStats[index === -1 ? 0 : index];
      };
      const setActiveNextBp = () => {
        addClassToNextItem(bps, 'active');
        setActiveBpData();
      };
      const hideSeparators = () => addClass(bps, 'hide-separators');
      const showSeparators = () => removeClass(bps, 'hide-separators');
      const hideHeaders = () => removeClass(bpsContainer, 'show-bp-headers');
      const moveUpHeaders = () => addClass(bps, 'move-up');
      const moveDownHeaders = () => removeClass(bps, 'move-up');
      const showBpDetailsContainer = () => addClass(bpDetailsContainer, 'active');
      const hideBpDetailsContainer = () => removeClass(bpDetailsContainer, 'active');

      const animCycle = async () => {
        showHeaders();
        showSeparators();
        hideBpDetailsContainer();
        await delay(500);
        showSeparators();
        await delay(5000);
        setActiveNextBp();
        await delay(300);
        hideSeparators();
        moveUpHeaders();
        await delay(1000);
        hideHeaders();
        await delay(1000);
        moveDownHeaders();
        showBpDetailsContainer();
        await delay(10000);
        hideSeparators();
      };

      animCycle();
      this.intervalReference = setInterval(animCycle, 24000);
    }
  }
};
</script>

<style src="./scoped.scss" lang="scss" scoped />
